import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../context/AuthContext.jsx";
import { STARTING_DEBT, EMERGENCY_FUND_GOAL } from "./data.js";
import Celebration from "../../components/Celebration.jsx";

const LEVEL_ID = "budget-commander";

function computeScore({ intelResult, campaignResult }) {
  const intelScore = intelResult.correctCount * 15;
  const zeroBasedBonus = 100;

  const emergencyProgress = Math.min(
    100,
    Math.round((campaignResult.emergencyFundBalance / EMERGENCY_FUND_GOAL) * 100)
  );

  const debtReduced = Math.max(0, STARTING_DEBT - campaignResult.debtBalance);
  const debtReductionBonus = Math.min(200, debtReduced);

  const goodChoices = campaignResult.log.filter((l) => l.choice.tag === "good").length;
  const badChoices = campaignResult.log.filter((l) => l.choice.tag === "bad").length;

  const choiceScore = goodChoices * 20 - badChoices * 15;
  const debtPenalty = Math.round(campaignResult.debtIncurred * 0.5);

  const raw = intelScore + zeroBasedBonus + emergencyProgress + debtReductionBonus + choiceScore - debtPenalty;
  return Math.max(50, raw);
}

export default function DebriefStage({ intelResult, plan, campaignResult, onRetry }) {
  const navigate = useNavigate();
  const { user, profile, refreshProfile } = useAuth();
  const [saving, setSaving] = useState(true);
  const [saved, setSaved] = useState(false);

  const score = computeScore({ intelResult, campaignResult });
  const debtChange = STARTING_DEBT - campaignResult.debtBalance;
  const debtWentUp = campaignResult.debtBalance > STARTING_DEBT - plan.debtPayoff;
  const didLevelUp = Math.floor(((profile?.xp ?? 0) + score) / 500) + 1 > (profile?.level ?? 1);

  useEffect(() => {
    let cancelled = false;

    async function save() {
      if (!user) {
        setSaving(false);
        return;
      }
      const newXp = (profile?.xp ?? 0) + score;
      const newLevel = Math.max(profile?.level ?? 1, Math.floor(newXp / 500) + 1);
      const newNetWorth = (profile?.net_worth_cents ?? 0) + score * 100;

      const { error } = await supabase
        .from("profiles")
        .update({ xp: newXp, level: newLevel, net_worth_cents: newNetWorth })
        .eq("id", user.id);

      if (!cancelled) {
        if (!error) {
          await refreshProfile();
          const { error: challengeError } = await supabase.rpc("record_level_result", {
            p_level_id: LEVEL_ID,
            p_score: score,
          });
          if (challengeError) {
            console.error("Failed to record challenge result:", challengeError.message);
          }
          setSaved(true);
        } else {
          console.error("Failed to save Budget Commander results:", error.message);
        }
        setSaving(false);
      }
    }

    save();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="animate-popIn text-center">
      <Celebration show={saved} levelUp={didLevelUp} />
      <h2 className="pixel-heading mb-1 text-lg text-lime">MISSION DEBRIEF</h2>
      <p className="mb-6 text-xs uppercase tracking-widest text-mist/50">
        {plan.goalName ? `Operation: ${plan.goalName}` : "Operation Complete"}
      </p>

      <div className="mx-auto mb-6 max-w-sm space-y-2 border-2 border-black bg-ink p-4 text-left text-sm">
        <div className="flex justify-between">
          <span className="text-mist/60">Field Intel Accuracy</span>
          <span className="text-cyan">{intelResult.correctCount}/10</span>
        </div>
        <div className="flex justify-between">
          <span className="text-mist/60">Zero-Based Plan</span>
          <span className="text-lime">✔ Every dollar assigned</span>
        </div>
        <div className="flex justify-between">
          <span className="text-mist/60">Emergency Fund</span>
          <span className="text-lime">
            ${campaignResult.emergencyFundBalance} / ${EMERGENCY_FUND_GOAL}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-mist/60">Debt Balance</span>
          <span className={debtWentUp ? "text-danger" : "text-lime"}>
            ${campaignResult.debtBalance}
            {!debtWentUp && debtChange > 0 ? ` (−$${debtChange})` : ""}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-mist/60">New Debt Incurred</span>
          <span className={campaignResult.debtIncurred > 0 ? "text-danger" : "text-lime"}>
            ${campaignResult.debtIncurred}
          </span>
        </div>
        <div className="flex justify-between border-t border-black/50 pt-2">
          <span className="text-mist/60">Goals Saved</span>
          <span className="text-gold">${campaignResult.goalsBalance}</span>
        </div>
        <div className="relative flex justify-between border-t border-black/50 pt-2 font-display text-[11px]">
          <span className="text-mist/60">SCORE</span>
          <span className="text-gold">{score.toLocaleString()}</span>
          {saved && (
            <span className="absolute -top-4 right-0 animate-floatUp text-[9px] text-lime">+{score} XP</span>
          )}
        </div>
      </div>

      <p className="mb-6 text-xs text-mist/50">
        {saving ? "Saving to your profile…" : saved ? "Net worth and XP updated on your profile ✅" : "Results ready."}
      </p>

      <div className="flex flex-wrap justify-center gap-3">
        <button onClick={onRetry} className="btn-magenta">
          ↻ RUN IT BACK
        </button>
        <button onClick={() => navigate("/")} className="btn-cyan">
          LEVEL SELECT
        </button>
      </div>
    </div>
  );
}
